import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = {
  name: "",
  email: "",
  role_title: "",
  department: "",
  office: "",
  phone: "",
  avatar: "",
  bio: "",
  manager: "",
};

const EDITABLE_FIELDS = Object.keys(EMPTY);

function pickEditableFields(emp) {
  if (!emp) return { ...EMPTY };
  return Object.fromEntries(EDITABLE_FIELDS.map((f) => [f, emp[f] || ""]));
}

function submitLabel({ saving, isEdit }) {
  if (saving) return "Saving…";
  return isEdit ? "Save changes" : "Add employee";
}

export default function EmployeeFormDialog({ open, onOpenChange, employee, onSaved }) {
  const isEdit = Boolean(employee?.id);
  const [form, setForm] = useState(() => pickEditableFields(employee));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(pickEditableFields(employee));
  }, [open, employee]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        // strip email — email is not editable after create
        const { email, ...rest } = form; // eslint-disable-line no-unused-vars
        const { data } = await api.patch(`/employees/${employee.id}`, rest);
        toast.success("Employee updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/employees", form);
        toast.success("Employee added");
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Edit employee" : "Add employee"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Update the employee's profile." : "Create a new employee record. Email is used as the unique identifier."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ef-name">Full name</Label>
              <Input
                id="ef-name"
                data-testid="ef-name"
                required
                value={form.name}
                onChange={set("name")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ef-email">Email</Label>
              <Input
                id="ef-email"
                data-testid="ef-email"
                required
                type="email"
                disabled={isEdit}
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ef-role">Role title</Label>
              <Input
                id="ef-role"
                data-testid="ef-role"
                required
                value={form.role_title}
                onChange={set("role_title")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ef-dept">Department</Label>
              <Input
                id="ef-dept"
                data-testid="ef-dept"
                required
                value={form.department}
                onChange={set("department")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ef-office">Office</Label>
              <Input
                id="ef-office"
                data-testid="ef-office"
                required
                value={form.office}
                onChange={set("office")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ef-phone">Phone</Label>
              <Input
                id="ef-phone"
                data-testid="ef-phone"
                value={form.phone}
                onChange={set("phone")}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="ef-avatar">Avatar URL <span className="text-[#8a8a80]">(optional)</span></Label>
              <Input
                id="ef-avatar"
                data-testid="ef-avatar"
                placeholder="https://…"
                value={form.avatar}
                onChange={set("avatar")}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="ef-manager">Reports to <span className="text-[#8a8a80]">(optional)</span></Label>
              <Input
                id="ef-manager"
                data-testid="ef-manager"
                value={form.manager}
                onChange={set("manager")}
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="ef-bio">Short bio</Label>
              <Textarea
                id="ef-bio"
                data-testid="ef-bio"
                value={form.bio}
                onChange={set("bio")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#e5e5e0]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="ef-submit"
              className="bg-[#4a6b53] text-white hover:bg-[#3d5a45]"
            >
              {submitLabel({ saving, isEdit })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
